import { useCallback, useState } from 'react'
import { fbt } from 'fbtee'
import { Plus, QrCode, Trash2 } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { useGroupTotems } from '../../hooks/useGroupTotems'
import { useUserProfiles } from '../../hooks/useUserProfiles'
import { canEditTotem, totemEditorIds } from '../../lib/totemPermissions'
import { cn } from '../../lib/utils'
import { DashedButton } from '../DashedButton'
import { EmptyState } from '../EmptyState'
import { ConfirmDialog } from '../ConfirmDialog'
import { TotemShareDialog } from '../TotemShareDialog'
import { ErrorState } from '../ErrorState'
import { Section } from '../Section'
import { borderLabel } from './borderStyles'
import { BorderSwatch } from './BorderSwatch'
import { TotemEditDialog, type TotemFormValues } from './TotemEditDialog'
import { FollowerCount, TotemFollowersDialog } from './TotemFollowersDialog'
import type { Group, Totem, UserProfile } from '../../types'

/**
 * A group's totems: the list (edit, share QR, followers, delete) and an Add
 * button, with their dialogs. The group page's Totems tab.
 */
export function GroupTotemsManager({ group }: { group: Group }) {
  const groupId = group.id
  const { user } = useAuth()
  const { totems, loading, error, createTotem, updateTotem, uploadTotemImage, removeTotem } =
    useGroupTotems(groupId)
  const profiles = useUserProfiles(group.memberIds)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingTotem, setEditingTotem] = useState<Totem | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [sharingTotem, setSharingTotem] = useState<Totem | null>(null)
  const [followersFor, setFollowersFor] = useState<Totem | null>(null)

  const canEdit = (totem: Totem) => canEditTotem(totem, group, user?.uid)

  const openCreate = useCallback(() => {
    setEditingTotem(null)
    setDialogOpen(true)
  }, [])

  const openEdit = useCallback((totem: Totem) => {
    setEditingTotem(totem)
    setDialogOpen(true)
  }, [])

  const handleSave = useCallback(
    async ({ emoji, name, borderStyle, editorIds, imageBlob }: TotemFormValues) => {
      if (editingTotem) {
        await updateTotem(editingTotem.id, {
          emoji, name, borderStyle, editorIds,
          ...(imageBlob === null ? { imageUrl: null } : {}),
        })
        if (imageBlob) await uploadTotemImage(editingTotem.id, imageBlob)
      } else if (user) {
        const newId = await createTotem(emoji, name, borderStyle, user.uid, editorIds, 'manage')
        if (imageBlob && newId) await uploadTotemImage(newId, imageBlob)
      }
      setDialogOpen(false)
    },
    [editingTotem, user, createTotem, updateTotem, uploadTotemImage],
  )

  const handleDelete = useCallback(async () => {
    if (!confirmDeleteId) return
    await removeTotem(confirmDeleteId)
    setConfirmDeleteId(null)
  }, [confirmDeleteId, removeTotem])

  if (error) return <ErrorState error={error} />

  const deletingTotem = confirmDeleteId ? totems.find(t => t.id === confirmDeleteId) : null

  return (
    <>
      <Section>
        {!loading && totems.length === 0 ? (
          <EmptyState>
            {fbt(
              'No totems yet. Add one so your crew can spot you in the crowd.',
              'Empty state for groups with no totems',
            )}
          </EmptyState>
        ) : (
          <ul className="flex flex-col gap-2">
            {totems.map(totem => (
              <li key={totem.id}>
                <TotemRow
                  totem={totem}
                  groupId={groupId}
                  editable={canEdit(totem)}
                  editorNames={editorNames(totem, profiles)}
                  onEdit={() => openEdit(totem)}
                  onShare={() => setSharingTotem(totem)}
                  onFollowers={() => setFollowersFor(totem)}
                  onDelete={() => setConfirmDeleteId(totem.id)}
                />
              </li>
            ))}
          </ul>
        )}

        <DashedButton onClick={openCreate} className="mt-2 flex items-center justify-center gap-2 px-4 py-3 text-sm text-muted-foreground hover:text-foreground">
          <Plus className="size-4" />
          <span>{fbt('Add totem', 'Button to add a new totem to the group')}</span>
        </DashedButton>
      </Section>

      {user && (
        <TotemEditDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          totem={editingTotem}
          group={group}
          currentUserId={user.uid}
          onSave={handleSave}
        />
      )}

      {sharingTotem && (
        <TotemShareDialog
          open
          onOpenChange={open => !open && setSharingTotem(null)}
          groupId={groupId}
          totem={sharingTotem}
          surface="manage"
        />
      )}

      {followersFor && (
        <TotemFollowersDialog
          open
          onOpenChange={open => !open && setFollowersFor(null)}
          groupId={groupId}
          totem={followersFor}
          canRemove={canEdit(followersFor)}
        />
      )}

      <ConfirmDialog
        open={!!confirmDeleteId}
        onOpenChange={open => !open && setConfirmDeleteId(null)}
        title={fbt('Delete totem?', 'Heading of the confirmation dialog before deleting a totem')}
        description={
          deletingTotem
            ? `${deletingTotem.emoji} ${deletingTotem.name}`
            : ''
        }
        confirmLabel={fbt('Delete', 'Confirm button in the delete totem dialog')}
        onConfirm={handleDelete}
      />
    </>
  )
}

function editorNames(totem: Totem, profiles: Record<string, UserProfile>): string {
  return totemEditorIds(totem)
    .map(uid => profiles[uid]?.displayName)
    .filter((n): n is string => !!n)
    .join(', ')
}

function TotemRow({
  totem,
  groupId,
  editable,
  editorNames,
  onEdit,
  onShare,
  onFollowers,
  onDelete,
}: {
  totem: Totem
  groupId: string
  editable: boolean
  editorNames: string
  onEdit: () => void
  onShare: () => void
  onFollowers: () => void
  onDelete: () => void
}) {
  return (
    <div
      role={editable ? 'button' : undefined}
      tabIndex={editable ? 0 : undefined}
      onClick={editable ? onEdit : undefined}
      onKeyDown={e => { if (e.key === 'Enter' && editable) onEdit() }}
      className={cn(
        'w-full flex items-center gap-3 rounded-lg border border-border px-3 py-3 text-left transition-colors',
        editable && 'hover:bg-secondary cursor-pointer',
      )}
    >
      <BorderSwatch style={totem.borderStyle} emoji={totem.emoji} imageUrl={totem.imageUrl} />
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium truncate">{totem.name}</span>
        <span className="block text-xs text-muted-foreground truncate">
          {borderLabel(totem.borderStyle)}
          {editorNames && ` · ${editorNames}`}
        </span>
      </span>
      <FollowerCount groupId={groupId} totemId={totem.id} onClick={onFollowers} />
      <button
        type="button"
        onClick={e => {
          e.stopPropagation()
          onShare()
        }}
        className="p-1 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
        aria-label={fbt('Share totem', 'Accessible label for the share totem button')}
      >
        <QrCode className="size-4" />
      </button>
      {editable && (
        <button
          type="button"
          onClick={e => {
            e.stopPropagation()
            onDelete()
          }}
          className="p-1 text-muted-foreground hover:text-destructive transition-colors cursor-pointer"
          aria-label={fbt('Delete totem', 'Accessible label for delete totem button')}
        >
          <Trash2 className="size-4" />
        </button>
      )}
    </div>
  )
}
